import type { Option } from '../year-picker'
import type { amountSchema } from './schema'
import type { FetchStatus, QueryStatus } from '@tanstack/react-query'
import type { z } from 'zod'

import * as Sentry from '@sentry/react'
import cx from 'classnames'
import { useEffect, useState } from 'react'
import { useMeasure } from 'react-use'

import { ErrorBanner } from '../../../shared/error-banner/error-banner'
import { DelayedLoading } from '../../table/delayed-loading'
import { formatterPrice } from '../../table/table'
import { YearPicker } from '../year-picker'

import styles from './amounts.module.css'
import { amountsSchema } from './schema'

export type Amount = z.infer<typeof amountSchema>

interface Props <> {
  amounts: Amount[] | null
  queryStatus: QueryStatus
  fetchStatus: FetchStatus
  severity: number | null
}

const severityTabColors = {
  1: 'green',
  2: 'yellow',
  3: 'orange',
  4: 'red'
}

const tabs: Array<{name: keyof Amount, label: string, color?: 'gray' | 'orange' | 'green' | 'purple'}> = [
  { name: 'charged_amount', label: 'Charged Amount', color: 'purple' },
  { name: 'ppo_discount', label: 'PPO Discount' },
  { name: 'ineligible', label: 'Ineligible' },
  { name: 'deductible', label: 'Deductible' },
  { name: 'out_of_pocket', label: 'Out of Pocket', color: 'orange' },
  { name: 'coinsurance', label: 'Co-Insurance' },
  { name: 'total_paid', label: 'Paid Claims', color: 'green' }
]

export function Amounts ({ amounts, queryStatus, fetchStatus, severity }: Props): JSX.Element {
  const [ wrapperRef, { width }] = useMeasure<HTMLDivElement>()

  let [ year, setYear ] = useState<number | null>(null)
  let [ amount, setAmount ] = useState<Amount | null>(null)
  let [ years, setYears ] = useState<Option[] | null>(null)
  let [ error, setError ] = useState<null | Error>(null)

  function getYears (amounts: Amount[]): Option[] {
    return amounts?.reduce<Option[]>((acc, amount) => {
      if (!amount.year) {
        return acc
      }

      return ([
        ...acc,
        {
          value: parseInt(amount.year),
          label: amount.year
        }
      ])
    }, [])
  }

  useEffect(() => {
    if (!amounts) {
      return
    }

    let validation = amountsSchema.safeParse(amounts)
    if (!validation.success) {
      Sentry.captureException(validation.error)
      setError(validation.error)
      return
    }

    let years = getYears(validation.data)

    if (year) {
      let currentAmount = validation.data.find((amount) => parseInt(amount.year) === year)
      currentAmount && setAmount(currentAmount)
      return
    }

    if (years.length > 0) {
      let sortedYear = years.sort((prev, next) => next.value - prev.value)
      let lastYear = sortedYear.at(0)

      if (lastYear) {
        setYear(lastYear.value)
      }

      setYears(years)
    }
  }, [ amounts, year ])

  function onChangeYear (id: number): void {
    setYear(id)
  }

  function formatAmount (input?: number | string | null): string | null {
    if (input === null || typeof input === 'undefined' || typeof input !== 'number') {
      return null
    }

    return formatterPrice.format(input)
  }

  function withYear (label: string): string {
    return `${year ?? ''} ${label}`
  }

  if (error) {
    return (
      <ErrorBanner
        title='Oops, something went wrong'
        subTitle='We are so sorry, but it looks like an error has occurred while loading claims data.'
      />
    )
  }

  let size: 'small' | 'medium' | 'large' =
    width < 1200
      ? 'small'
      : width < 1500
        ? 'medium'
        : 'large'

  return (
    <div
      className={cx(styles.amounts, styles[size])}
      ref={wrapperRef}
    >
      <div className={styles.yearWrapper}>
        {years && year && (
          <YearPicker
            options={years}
            onChange={onChangeYear}
            selected={year}
          />
        )}
        <span className={styles.selectedYear}>{year}</span>
      </div>
      <div className={styles.tabContainer}>
        <DelayedLoading loading={queryStatus === 'pending' || fetchStatus === 'fetching'} />
        {queryStatus === 'success' && amounts &&
            tabs.map(({ name, label, color }) => (
              <HeaderTab
                key={name}
                label={withYear(label)}
                amount={formatAmount(amount?.[name] ?? null)}
                color={color ?? undefined}
              />
            ))
        }
      </div>
      {queryStatus === 'success' && amounts && (
        <div
          // @ts-expect-error //it's okay
          className={cx(styles.severity, styles[severityTabColors?.[severity]])}
        >
          <label
            htmlFor='severity'
            className={styles.severityLabel}
          >
            Severity
          </label>
          <div id='severity'>
            <p className={cx(styles.severityValue)}>{severity ?? 0}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

interface HeaderTabsProps {
  amount: string | null
  label: string
  color?: 'gray' | 'orange' | 'green' | 'purple'
}

export function HeaderTab ({ amount, label, color = 'gray' }: HeaderTabsProps): JSX.Element | null {
  return (
    <div className={styles.tabWrapper}>
      <div className={cx(styles.tab, styles[color])}>
        <label
          htmlFor='amount'
          className={styles.amountLabel}
        >
          {label}
        </label>
        <p
          id='amount'
          className={styles.amountValue}
        >
          {amount === null ? 'None' : amount}
        </p>
      </div>
    </div>
  )
}
